import { useAuth } from '@clerk/clerk-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'

import type { DutyBranch } from '@/features/duties/branches'
import {
  excludeDutyTool,
  excludeDutyMcpConnection,
  fetchBenefitsCalQuestionnaireSchema,
  fetchDutyTools,
  fetchDutyMcpConnections,
  includeDutyTool,
  includeDutyMcpConnection,
  patchDutyToolOverrides,
} from '@/features/duties/api/toolbox/toolbox'
import {
  createMcpConnection,
  completeMcpOauth,
  createTool,
  deleteMcpConnection,
  deleteTool,
  fetchMcpConnections,
  startMcpOauth,
  testMcpConnection,
  fetchOrgTools,
  updateMcpConnection,
  updateTool,
} from '@/features/tools/api/api'
import type {
  AppToolWrite,
  McpConnectionWrite,
  DutyToolOverrideWrite,
  WebhookAppToolWrite,
} from '@/features/tools/types'

const NO_AUTH_TOKEN_ERROR = 'No authentication token available'

export const orgToolsQueryKey = ['org-tools'] as const
export const mcpConnectionsQueryKey = ['mcp-connections'] as const
export const dutyToolsQueryKey = (
  dutyId: string,
  branch: DutyBranch | null = null
) => ['duty-tools', dutyId, branch] as const
export const dutyMcpConnectionsPrefix = (dutyId: string) =>
  ['duty-mcp-connections', dutyId] as const
export const dutyMcpConnectionsQueryKey = (
  dutyId: string,
  branch: DutyBranch | null = null
) => [...dutyMcpConnectionsPrefix(dutyId), branch] as const

function invalidateMcpConnectionQueries(queryClient: QueryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: mcpConnectionsQueryKey }),
    queryClient.invalidateQueries({ queryKey: ['duty-mcp-connections'] }),
  ])
}

export function useDutyMcpConnections(
  dutyId: string,
  enabled = true,
  branch: DutyBranch | null = null
) {
  const { getToken } = useAuth()
  return useQuery({
    queryKey: dutyMcpConnectionsQueryKey(dutyId, branch),
    enabled: enabled && !!dutyId,
    staleTime: 15_000,
    queryFn: async ({ signal }) => {
      const token = await getToken()
      if (!token) throw new Error(NO_AUTH_TOKEN_ERROR)
      return fetchDutyMcpConnections(token, dutyId, signal, branch)
    },
  })
}

export function useSetDutyMcpConnection(
  dutyId: string,
  branch: DutyBranch | null = null
) {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      connectionId,
      enabled,
    }: {
      connectionId: string
      enabled: boolean
    }) => {
      const token = await getToken()
      if (!token) throw new Error(NO_AUTH_TOKEN_ERROR)
      if (enabled) {
        await includeDutyMcpConnection(token, dutyId, connectionId, branch)
      } else {
        await excludeDutyMcpConnection(token, dutyId, connectionId, branch)
      }
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: dutyMcpConnectionsPrefix(dutyId),
      }),
  })
}

export function useBenefitsCalQuestionnaireSchema(enabled: boolean) {
  const { getToken } = useAuth()

  return useQuery({
    queryKey: ['benefitscal-questionnaire-schema'],
    enabled,
    staleTime: 5 * 60_000,
    queryFn: async ({ signal }) => {
      const token = await getToken()
      if (!token) throw new Error(NO_AUTH_TOKEN_ERROR)
      return fetchBenefitsCalQuestionnaireSchema(token, signal)
    },
  })
}

export function useOrgTools(enabled: boolean = true) {
  const { getToken } = useAuth()

  return useQuery({
    queryKey: orgToolsQueryKey,
    enabled,
    staleTime: 30_000,
    queryFn: async ({ signal }) => {
      const token = await getToken()
      if (!token) throw new Error(NO_AUTH_TOKEN_ERROR)
      return fetchOrgTools(token, signal)
    },
  })
}

export function useCreateTool() {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (tool: WebhookAppToolWrite) => {
      const token = await getToken()
      if (!token) throw new Error(NO_AUTH_TOKEN_ERROR)
      return createTool(token, tool)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orgToolsQueryKey })
      // A newly created org tool should immediately show up as includable
      // on any already-loaded duty's Tools card, not just after its cache
      // naturally expires (matches useUpdateTool/useDeleteTool below).
      queryClient.invalidateQueries({ queryKey: ['duty-tools'] })
    },
  })
}

export function useUpdateTool() {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      toolId,
      tool,
    }: {
      toolId: string
      tool: Partial<AppToolWrite>
    }) => {
      const token = await getToken()
      if (!token) throw new Error(NO_AUTH_TOKEN_ERROR)
      return updateTool(token, toolId, tool)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orgToolsQueryKey })
      // A tool's effective duty-level config may inherit org fields, so an
      // org-level edit can change what an unoverridden duty card shows.
      queryClient.invalidateQueries({ queryKey: ['duty-tools'] })
    },
  })
}

export function useDeleteTool() {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (toolId: string) => {
      const token = await getToken()
      if (!token) throw new Error(NO_AUTH_TOKEN_ERROR)
      return deleteTool(token, toolId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orgToolsQueryKey })
      // Deleting an org tool cascades to any duty inclusions/overrides on
      // the backend, so duty-tool lists showing it must also refresh.
      queryClient.invalidateQueries({ queryKey: ['duty-tools'] })
    },
  })
}

export function useMcpConnections() {
  const { getToken } = useAuth()
  return useQuery({
    queryKey: mcpConnectionsQueryKey,
    staleTime: 30_000,
    queryFn: async ({ signal }) => {
      const token = await getToken()
      if (!token) throw new Error(NO_AUTH_TOKEN_ERROR)
      return fetchMcpConnections(token, signal)
    },
  })
}

export function useCreateMcpConnection() {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (connection: McpConnectionWrite) => {
      const token = await getToken()
      if (!token) throw new Error(NO_AUTH_TOKEN_ERROR)
      return createMcpConnection(token, connection)
    },
    onSuccess: () => invalidateMcpConnectionQueries(queryClient),
  })
}

export function useUpdateMcpConnection() {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      connectionId,
      connection,
    }: {
      connectionId: string
      connection: Partial<McpConnectionWrite>
    }) => {
      const token = await getToken()
      if (!token) throw new Error(NO_AUTH_TOKEN_ERROR)
      return updateMcpConnection(token, connectionId, connection)
    },
    onSuccess: () => invalidateMcpConnectionQueries(queryClient),
  })
}

export function useDeleteMcpConnection() {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (connectionId: string) => {
      const token = await getToken()
      if (!token) throw new Error(NO_AUTH_TOKEN_ERROR)
      return deleteMcpConnection(token, connectionId)
    },
    onSuccess: () => invalidateMcpConnectionQueries(queryClient),
  })
}

export function useStartMcpOauth() {
  const { getToken } = useAuth()
  return useMutation({
    mutationFn: async ({
      connectionId,
      redirectUri,
    }: {
      connectionId: string
      redirectUri: string
    }) => {
      const token = await getToken()
      if (!token) throw new Error(NO_AUTH_TOKEN_ERROR)
      return startMcpOauth(token, connectionId, redirectUri)
    },
  })
}

export function useCompleteMcpOauth() {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ code, state }: { code: string; state: string }) => {
      const token = await getToken()
      if (!token) throw new Error(NO_AUTH_TOKEN_ERROR)
      return completeMcpOauth(token, code, state)
    },
    onSuccess: () => invalidateMcpConnectionQueries(queryClient),
  })
}

export function useTestMcpConnection() {
  const { getToken } = useAuth()
  return useMutation({
    mutationFn: async (connectionId: string) => {
      const token = await getToken()
      if (!token) throw new Error(NO_AUTH_TOKEN_ERROR)
      return testMcpConnection(token, connectionId)
    },
  })
}

export function useDutyTools(
  dutyId: string,
  enabled: boolean = true,
  branch: DutyBranch | null = null
) {
  const { getToken } = useAuth()

  return useQuery({
    queryKey: dutyToolsQueryKey(dutyId, branch),
    enabled: enabled && !!dutyId,
    staleTime: 15_000,
    queryFn: async ({ signal }) => {
      const token = await getToken()
      if (!token) throw new Error(NO_AUTH_TOKEN_ERROR)
      return fetchDutyTools(token, dutyId, branch, signal)
    },
  })
}

export function useIncludeDutyTool(
  dutyId: string,
  branch: DutyBranch | null = null
) {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      toolId,
      overrides,
    }: {
      toolId: string
      overrides?: DutyToolOverrideWrite
    }) => {
      const token = await getToken()
      if (!token) throw new Error(NO_AUTH_TOKEN_ERROR)
      return includeDutyTool(token, dutyId, toolId, overrides, branch)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: dutyToolsQueryKey(dutyId, branch),
      })
    },
  })
}

export function usePatchDutyTool(
  dutyId: string,
  branch: DutyBranch | null = null
) {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      toolId,
      overrides,
    }: {
      toolId: string
      overrides: DutyToolOverrideWrite
    }) => {
      const token = await getToken()
      if (!token) throw new Error(NO_AUTH_TOKEN_ERROR)
      return patchDutyToolOverrides(token, dutyId, toolId, overrides, branch)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: dutyToolsQueryKey(dutyId, branch),
      })
    },
  })
}

export function useExcludeDutyTool(
  dutyId: string,
  branch: DutyBranch | null = null
) {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (toolId: string) => {
      const token = await getToken()
      if (!token) throw new Error(NO_AUTH_TOKEN_ERROR)
      return excludeDutyTool(token, dutyId, toolId, branch)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: dutyToolsQueryKey(dutyId, branch),
      })
    },
  })
}

export type {
  AppTool,
  AppToolWrite,
  DutyToolListItem,
  DutyToolOverrideWrite,
  DutyToolOverrides,
  DutyToolEffective,
  JsonSchemaObject,
  McpConnection,
  McpConnectionTestResult,
  McpConnectionWrite,
  ToolParameterField,
  ToolParameterType,
} from '@/features/tools/types'
export {
  argsSchemaToParameters,
  parametersToArgsSchema,
  EMPTY_ARGS_SCHEMA,
} from '@/features/tools/schema'
